import React, { useEffect, useState } from 'react';
import { adminApi, fmtDate } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StatusPill } from '@/components/fintech/StatusPill';
import { CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminKycQueuePage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState('');

  const load = () => adminApi.get('/admin/kyc/queue').then(r => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const submitReview = async () => {
    if (!selected || !decision) return;
    try {
      await adminApi.post(`/admin/kyc/${selected.user_id}/review`, { decision, reason });
      toast.success(`KYC ${decision}d`);
      setSelected(null); setDecision(null); setReason(''); load();
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">KYC queue</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(k => (
          <Card key={k.session_id} className="bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="font-semibold">{k.user?.first_name} {k.user?.last_name}</div>
                <StatusPill status={k.status} />
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{k.user?.email}</div>
              <div className="mt-3 text-sm space-y-1">
                <div>Type: <span className="font-medium">{k.document_type}</span></div>
                {k.document_number && <div>Number: <code className="text-xs">{k.document_number}</code></div>}
                {k.issuing_country && <div>Country: {k.issuing_country}</div>}
                <div>Submitted: {fmtDate(k.submitted_at)}</div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" onClick={() => { setSelected(k); setDecision('approve'); }} data-testid={`kyc-approve-${k.user_id}`} className="bg-[hsl(152_45%_34%)] hover:bg-[hsl(152_45%_28%)]"><CheckCircle2 className="h-4 w-4 mr-1" />Approve</Button>
                <Button size="sm" variant="destructive" onClick={() => { setSelected(k); setDecision('reject'); }} data-testid={`kyc-reject-${k.user_id}`}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {items.length === 0 && <Card className="bg-white"><CardContent className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No pending KYC requests</CardContent></Card>}

      <Dialog open={!!selected} onOpenChange={o => !o && (setSelected(null), setDecision(null))}>
        <DialogContent>
          <DialogHeader><DialogTitle>{decision === 'approve' ? 'Approve' : 'Reject'} KYC</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">{selected?.user?.email} — {selected?.user?.first_name} {selected?.user?.last_name}</div>
            <Textarea placeholder={decision === 'reject' ? 'Reason (required)' : 'Optional note'} value={reason} onChange={e => setReason(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
            <Button onClick={submitReview} className={decision === 'approve' ? 'bg-[hsl(152_45%_34%)]' : 'bg-[hsl(var(--destructive))]'}>{decision === 'approve' ? 'Approve' : 'Reject'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
